/**
 * 项目支出新增/编辑共用一个收款方选择区：直接选择库内分包商，或创建后自动选中。
 * 历史收款名称可原样保留；仅录入支票号，不再重复录入发票号，沿用现有手机布局和保存权限。
 * 历史备注中的明确支票号可自动补入空白号码栏；用户确认保存才写账，不批量改写历史数据。
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Loader2, Plus, Save, Trash2, X } from "lucide-react";
import { extractCheckNumberFromNote } from "./outflowCheckNumber";
import { SubcontractCompanyPicker } from "../subcontract/SubcontractCompanyPicker";
import { OutflowAttachmentManager } from "./OutflowAttachmentManager";

const inp = "mt-1 w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 bg-white";
export const err = (e) => e?.response?.data?.detail || e.message;

/* 支出明细 新增 / 编辑：付款日期 · 收款方 · 阶段 · 科目 · 方式 · 支票号 · 金额 · 备注 */
export const OutflowDialog = ({ api, pid, zh, row, meta, onClose, onSaved, onDelete, onAttachmentsChanged, preset }) => {
  const p = row || preset || {};
  // 已登记号码和手动修改优先；只对支票方式的空白号码做备注识别，不自动写回历史账目。
  const checkNoManual = useRef(Boolean(String(p.checkNo ?? "").trim()));
  const fillCheckNo = (form) => checkNoManual.current ? form.checkNo
    : (form.method === "check" ? extractCheckNumberFromNote(form.note) : "");
  const [f, setF] = useState({
    date: p.date || new Date().toISOString().slice(0, 10), payee: p.payee || "", phaseKey: p.phaseKey || "", category: p.category || "subcontractor",
    method: p.method || "check", checkNo: checkNoManual.current ? String(p.checkNo) : ((p.method || "check") === "check" ? extractCheckNumberFromNote(p.note) : ""),
    amount: row ? String(row.amount) : (p.amount ? String(p.amount) : ""), note: p.note || "",
  });
  const [busy, setBusy] = useState(false);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  // 公司候选层需位于所属弹窗内，沿用新增分包的做法，避免模态滚动锁拦截候选列表滚轮。
  const [dialogContentNode, setDialogContentNode] = useState(null);
  const dialogContentRef = useCallback(node => setDialogContentNode(node), []);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [companyBusy, setCompanyBusy] = useState(false);
  const [canManageCompanies, setCanManageCompanies] = useState(false);
  const [trades, setTrades] = useState(null);
  const [companyDraft, setCompanyDraft] = useState({ trade: "", name: "", legalName: "" });
  const locked = busy || companyBusy || attachmentBusy;
  useEffect(() => {
    let alive = true;
    api.get("/subcontractor-companies/can-manage").then(({ data }) => {
      if (alive) setCanManageCompanies(data?.canManage === true);
    }).catch(() => { if (alive) setCanManageCompanies(false); });
    return () => { alive = false; };
  }, [api]);
  const set = (k) => (e) => {
    const value = e.target.value;
    // 手动清空也是明确选择，不能在保存或修改备注时又填回来。
    if (k === "checkNo") checkNoManual.current = true;
    setF((o) => {
      const next = { ...o, [k]: value };
      return k === "method" ? { ...next, checkNo: fillCheckNo(next) } : next;
    });
  };
  const openCompanyCreation = async () => {
    setCompanyBusy(true);
    try {
      if (!trades) {
        const { data } = await api.get("/subcontract-trades");
        setTrades(Array.isArray(data) ? data : []);
      }
      setCreatingCompany(true);
    } catch (e) { toast.error(err(e)); }
    finally { setCompanyBusy(false); }
  };
  // 登记公司与登记项目支出是两次明确保存；创建成功只带入收款名称，不提前写入项目费用。
  const createCompany = async () => {
    if (!companyDraft.trade || !companyDraft.name.trim()) return toast.error(zh ? "请选择工种并填写公司名称" : "Trade and company name required");
    setCompanyBusy(true);
    try {
      const { data } = await api.post("/subcontractor-companies", { ...companyDraft, name: companyDraft.name.trim(), legalName: companyDraft.legalName.trim() });
      setSelectedCompany(data);
      setF((o) => ({ ...o, payee: data.legalName || data.name }));
      setCreatingCompany(false);
      setCompanyDraft({ trade: "", name: "", legalName: "" });
      toast.success(zh ? "分包商已登记并填入收款方" : "Subcontractor added and selected");
    } catch (e) { toast.error(err(e)); }
    setCompanyBusy(false);
  };
  // 备注输入结束再识别，避免只输入到“2”就抢先锁定；自动值随备注纠正，手填值始终保留。
  const fillFromNote = () => setF((o) => ({ ...o, checkNo: fillCheckNo(o) }));
  const save = async () => {
    if (!f.payee.trim() || !(Number(f.amount) > 0) || !f.date) return toast.error(zh ? "请填写付款日期、收款方与金额" : "Date, payee & amount required");
    setBusy(true);
    try {
      // 发票号已退出此表单：省略字段，让后端保留历史值，避免隐藏字段被清空或从备注暗中改写。
      const body = { ...f, checkNo: fillCheckNo(f), amount: Number(f.amount), phaseKey: f.phaseKey || null };
      if (row) await api.put(`/finance/project/${pid}/outflows/${row.kind}/${row.id}`, body);
      else await api.post(`/finance/project/${pid}/outflows`, body);
      toast.success(zh ? (row ? "已更新支出记录 ✓" : "已新增支出记录 ✓") : "Saved");
      onSaved();
    } catch (e) { toast.error(err(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={open => { if (!open && !locked) onClose(); }}>
      <DialogContent ref={dialogContentRef} closeDisabled={locked} closeLabel={zh ? "关闭" : "Close"} className="max-w-lg" data-testid="outflow-dialog" data-mode={row ? "edit" : "new"}>
        <DialogHeader>
          <DialogTitle>{row ? (zh ? "编辑支出记录" : "Edit payment") : (zh ? "新增支出记录" : "New payment")}</DialogTitle>
          <DialogDescription>{zh ? "直接登记一笔项目付款（手工费用）；保存后计入支出明细合计与项目成本。" : "Record a project payment; counted in expenses and project cost."}</DialogDescription>
        </DialogHeader>
        <fieldset disabled={locked} className="grid min-w-0 grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className="text-xs text-slate-500">{zh ? "付款日期 *" : "Date *"}</span><input data-testid="outflow-date" type="date" className={inp} value={f.date} onChange={set("date")} /></label>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "付款金额 * ($)" : "Amount *"}</span><input data-testid="outflow-amount" type="number" step="0.01" className={inp} value={f.amount} onChange={set("amount")} /></label>
          <div className="block sm:col-span-2 space-y-2">
            {/* 收款方与公司库合为同一个选择控件；历史账目仅有名称时仍展示原值，不按同名猜测公司身份。 */}
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-0 basis-[220px] flex-1" data-testid="outflow-payee">
                <SubcontractCompanyPicker api={api} value={selectedCompany} displayValue={f.payee} disabled={locked}
                  label={<span className="text-xs text-slate-500">{zh ? "收款方 *" : "Payee *"}</span>}
                  placeholder={zh ? "从分包商库选择" : "Choose a subcontractor"} allowManual={false} selectedHint={false}
                  emptyMessage={canManageCompanies ? (zh ? "没有匹配的分包商，可创建新分包商。" : "No matching subcontractor. You can add one.") : (zh ? "没有匹配的分包商。" : "No matching subcontractor.")}
                  portalContainer={dialogContentNode} onChange={(company) => { if (company) { setSelectedCompany(company); setF((o) => ({ ...o, payee: company.legalName || company.name })); } }} zh={zh} />
              </div>
              {canManageCompanies && <Button type="button" size="sm" variant="outline" className="h-9 shrink-0" onClick={openCompanyCreation} disabled={locked} data-testid="outflow-create-subcontractor"><Plus aria-hidden="true" className="h-3.5 w-3.5 mr-1" />{zh ? "创建新分包商" : "Add subcontractor"}</Button>}
            </div>
            {creatingCompany && <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="outflow-subcontractor-create-form">
              <div className="flex items-center justify-between gap-2"><span className="text-xs font-semibold text-slate-700">{zh ? "新增分包商到公司库" : "Add subcontractor to directory"}</span><button type="button" aria-label={zh ? "关闭" : "Close"} className="rounded p-1 text-slate-500 hover:bg-white" onClick={() => setCreatingCompany(false)}><X className="h-4 w-4" /></button></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block"><span className="text-xs text-slate-500">{zh ? "工种 *" : "Trade *"}</span><select className={inp} data-testid="outflow-new-sub-trade" value={companyDraft.trade} onChange={(e) => setCompanyDraft((o) => ({ ...o, trade: e.target.value }))}><option value="">{zh ? "请选择" : "Select"}</option>{(trades || []).map((trade) => <option key={trade.key} value={trade.key}>{trade.name}</option>)}</select></label>
                <label className="block"><span className="text-xs text-slate-500">{zh ? "公司名称 *" : "Company *"}</span><input className={inp} data-testid="outflow-new-sub-name" value={companyDraft.name} onChange={(e) => setCompanyDraft((o) => ({ ...o, name: e.target.value }))} /></label>
                <label className="block sm:col-span-2"><span className="text-xs text-slate-500">{zh ? "支票收款名称（选填）" : "Check payee name (optional)"}</span><input className={inp} data-testid="outflow-new-sub-legal-name" value={companyDraft.legalName} onChange={(e) => setCompanyDraft((o) => ({ ...o, legalName: e.target.value }))} /></label>
              </div>
              <div className="flex justify-end gap-2"><Button type="button" size="sm" variant="outline" onClick={() => setCreatingCompany(false)} disabled={companyBusy}>{zh ? "取消" : "Cancel"}</Button><Button type="button" size="sm" onClick={createCompany} disabled={companyBusy} data-testid="outflow-new-sub-save">{companyBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存并选择" : "Save & select"}</Button></div>
              <p className="text-[11px] text-slate-500">{zh ? "保存到分包商库后，可在分包商管理中补充联系方式和资质。" : "After saving, complete contact and license details in the subcontractor directory."}</p>
            </div>}
          </div>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "施工阶段" : "Phase"}</span>
            <select data-testid="outflow-phase" className={inp} value={f.phaseKey} onChange={set("phaseKey")}>
              <option value="">{zh ? "不指定" : "—"}</option>
              {(meta?.phases || []).map((p) => <option key={p.key} value={p.key}>{p.no} {zh ? p.zh : p.en}</option>)}
            </select></label>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "成本科目" : "Category"}</span>
            <select data-testid="outflow-category" className={inp} value={f.category} onChange={set("category")}>
              {(meta?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select></label>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "支付方式" : "Method"}</span>
            <select data-testid="outflow-method" className={inp} value={f.method} onChange={set("method")}>
              {(meta?.methods || []).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select></label>
          <label className="block"><span className="text-xs text-slate-500">{zh ? "支票号" : "Check #"}</span><input data-testid="outflow-checkno" className={inp} value={f.checkNo} onChange={set("checkNo")} placeholder="#1021" /></label>
          <label className="block sm:col-span-2"><span className="text-xs text-slate-500">{zh ? "备注" : "Note"}</span><input data-testid="outflow-note" className={inp} value={f.note} onChange={set("note")} onBlur={fillFromNote} /></label>
        </fieldset>
        {row && <OutflowAttachmentManager api={api} pid={pid} row={row} zh={zh} disabled={busy || companyBusy} onChanged={onAttachmentsChanged} onBusyChange={setAttachmentBusy} />}
        <div className="flex-wrap md:flex-nowrap flex justify-end gap-2 mt-2">
          {row && !row.locked && onDelete && <Button type="button" variant="outline" size="sm" className="mr-auto text-red-600" disabled={locked} onClick={() => onDelete(row)} data-testid="outflow-edit-delete"><Trash2 className="mr-1 h-3.5 w-3.5" />{zh ? "删除记录" : "Delete payment"}</Button>}
          <Button variant="outline" size="sm" onClick={onClose} disabled={locked} data-testid="outflow-cancel">{zh ? "取消" : "Cancel"}</Button>
          <Button size="sm" onClick={save} disabled={locked || creatingCompany} data-testid="outflow-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}{zh ? "保存" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
